import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Sparkles, BookOpen, Lock, ChevronDown } from "lucide-react";
import { DASH } from "@/constants/testIds";
import { InfoTip } from "@/components/InfoTip";
import { FormatHelp } from "@/components/FormatHelp";
import { useL } from "@/lib/useL";

const OPTIONS_KEY = "sux1_show_options";

const EN_LABELS = {
  short: "Short", medium: "Medium", long: "Long",
  paragraph: "Paragraph", bullets: "Key points", both: "Paragraph + key points",
  enseignant: "Teacher (5 sections + pitfalls)", revision: "Revision sheet (pupil)", revision_dys: "DYS revision sheet (adapted)",
  metaphore: "Metaphors (illustrated summary)", poeme: "Poem and recitation", biographie: "Encyclopedic sheet",
  philo: "Philosophy + quotes", solve: "Explanation & solution", dissertation: "Essay (French dissertation)",
  auto: "Automatic", formal: "Formal", standard: "Standard", casual: "Casual",
};

/**
 * Local <select> primitive used across every config row.
 * Extracted alongside the panel so Dashboard.jsx no longer owns it.
 */
const SelectField = ({
  label,
  value,
  onChange,
  options,
  testid,
  disabledKeys = [],
  note = null,
  tooltip = null,
}) => {
  const unavailable = useL()(" — indisponible ici", " — not available here");
  const ready = Array.isArray(options) && options.length > 0;
  const dset = new Set(disabledKeys);
  return (
    <div className="flex flex-col gap-2">
      <label className="sux-marker flex items-center gap-1.5">
        <span>{label}</span>
        {tooltip && (
          <InfoTip
            content={tooltip}
            testid={testid ? `${testid}-info` : undefined}
          />
        )}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          disabled={!ready}
          className="w-full appearance-none bg-card border border-border px-4 py-3 pr-10 font-['IBM_Plex_Sans'] text-[14px] focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7] disabled:text-muted-foreground transition-colors hover:border-[#002FA7]/40"
        >
          {!ready && <option>loading…</option>}
          {ready &&
            options.map((o) => (
              <option key={o.key} value={o.key} disabled={dset.has(o.key)}>
                {o.label}
                {dset.has(o.key) ? unavailable : ""}
              </option>
            ))}
        </select>
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-xs">
          ▾
        </span>
      </div>
      {note && (
        <p
          data-testid={`${testid}-note`}
          className="font-mono uppercase tracking-[0.15em] text-[10px] leading-relaxed text-[#002FA7]"
        >
          {note}
        </p>
      )}
    </div>
  );
};

/**
 * Right-column "Calibrate the output" panel on the Dashboard.
 *
 * Extracted from `Dashboard.jsx` to tame the monolith. Pure UI, driven
 * entirely by props — no state, no business logic. Dashboard.jsx keeps
 * owning model/length/format/… state and passes setters down.
 */
export default function OutputConfigPanel({
  tab,
  TAB_BOOK,
  model,
  setModel,
  length,
  setLength,
  format,
  setFormat,
  formatDisabled = false,
  audienceLevel,
  setAudienceLevel,
  ficheType,
  setFicheType,
  language,
  setLanguage,
  register,
  setRegister,
  mode,
  setMode,
  meta,
  disabledModels,
  modelNote,
  isPro,
  chapterPreview,
  onSummarize,
  busy,
  text,
  online,
  simpleDefault = false,
}) {
  const { t } = useTranslation();
  const L = useL();
  const tr = (list) => L(list, list.map((o) => ({ ...o, label: EN_LABELS[o.key] || o.label })));
  const [choice, setChoice] = React.useState(() => localStorage.getItem(OPTIONS_KEY));
  const showOptions = choice === null ? !simpleDefault : choice === "1";
  const toggleOptions = () => { const v = showOptions ? "0" : "1"; localStorage.setItem(OPTIONS_KEY, v); setChoice(v); };

  return (
    <aside
      className={`col-span-1 lg:col-span-4 border-r border-b border-border bg-secondary/40 p-6 lg:p-8 flex flex-col gap-6 min-h-[60vh] ${
        tab === TAB_BOOK ? "hidden lg:flex" : ""
      }`}
    >
      <div>
        <div className="sux-marker mb-2">{t("dashboard.config_marker")}</div>
        <h2
          className="text-2xl font-black tracking-tight"
          style={{ fontFamily: "Outfit" }}
        >
          {t("dashboard.config_title")}
        </h2>
        {tab === TAB_BOOK && (
          <p className="text-xs text-muted-foreground mt-2">
            {L("Le scan de livre utilise le modèle IA, la langue, le registre et le niveau de classe.", "Book scan uses the AI model, language, register and class level.")}
          </p>
        )}
      </div>

      <button type="button" onClick={toggleOptions} data-testid="config-more-options" aria-expanded={showOptions}
        className="flex items-center justify-between gap-2 border border-border bg-card px-4 py-3 text-left hover:border-[#002FA7] transition-colors">
        <span>
          <span className="block font-mono uppercase tracking-[0.14em] text-[11px] text-[#002FA7]">
            {showOptions ? L("Moins d'options", "Fewer options") : L("Plus d'options", "More options")}
          </span>
          {!showOptions && (
            <span className="block text-xs text-muted-foreground mt-1" data-testid="config-current-summary">
              {L("Réglages actuels : ", "Current settings: ")}{(meta.models.find((m) => m.key === model)?.label || model).split(" · ")[0]} · {tr(meta.lengths).find((o) => o.key === length)?.label || length} · {tr(meta.formats).find((o) => o.key === format)?.label || format}
            </span>
          )}
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${showOptions ? "rotate-180" : ""}`} />
      </button>

      {showOptions && (<>
      <SelectField
        label={t("dashboard.ai_model")}
        value={model}
        onChange={setModel}
        options={meta.models.map((m) => ({ ...m, label: t(`model_labels.${m.key}`, m.label) }))}
        testid={DASH.modelSelect}
        disabledKeys={disabledModels}
        note={modelNote}
        tooltip={L(
          <div className="space-y-1">
            <div><b>Gemini 3 Flash</b> (1 crédit) — rapide et fiable, idéal pour la plupart des textes et les documents longs.</div>
            <div><b>Claude Sonnet 4.5</b> (2 crédits) — le français le plus naturel : fiches soignées, DYS, textes littéraires.</div>
            <div><b>GPT-5.2</b> (2 crédits) — le plus précis pour les sciences, l'économie, le droit et les textes techniques.</div>
            <div className="pt-1"><b>Pas sûr ?</b> Prenez Gemini. Passez à Claude ou GPT-5.2 seulement pour un rendu supérieur.</div>
          </div>,
          <div className="space-y-1">
            <div><b>Gemini 3 Flash</b> (1 credit) — fast and reliable, ideal for most texts and long documents.</div>
            <div><b>Claude Sonnet 4.5</b> (2 credits) — the most natural writing: polished sheets, DYS, literary texts.</div>
            <div><b>GPT-5.2</b> (2 credits) — the most precise for science, economics, law and technical texts.</div>
            <div className="pt-1"><b>Not sure?</b> Pick Gemini. Switch to Claude or GPT-5.2 only for a higher-end result.</div>
          </div>,
        )}
      />
      {tab !== TAB_BOOK && (
        <SelectField
          label={t("dashboard.length")}
          value={length}
          onChange={setLength}
          options={tr(meta.lengths)}
          testid={DASH.lengthSelect}
          tooltip={L("Court : 3 à 5 lignes · Moyen : un paragraphe dense · Long : 2 à 3 paragraphes détaillés.", "Short: 3 to 5 lines · Medium: one dense paragraph · Long: 2 to 3 detailed paragraphs.")}
        />
      )}
      {tab !== TAB_BOOK && (
        <div className={formatDisabled ? "opacity-50 pointer-events-none" : ""}>
          <SelectField
            label={formatDisabled ? `${t("dashboard.format")} · ${L("remplacé par l'instruction", "replaced by the instruction")}` : t("dashboard.format")}
            value={format}
            onChange={setFormat}
            options={tr(meta.formats)}
            testid={DASH.formatSelect}
            tooltip={L(
              <div className="space-y-1">
                <div>
                  <b>Paragraphe</b> — résumé rédigé en un ou plusieurs
                  paragraphes fluides.
                </div>
                <div>
                  <b>Points clés</b> — liste à puces des idées essentielles.
                </div>
                <div>
                  <b>Paragraphe + points</b> — les deux réunis, format le plus
                  complet.
                </div>
                <div>
                  <b>Enseignant</b> — fiche pédagogique 5 sections (résumé,
                  étapes, notions, concepts, pièges) prête à préparer un cours.
                </div>
                <div>
                  <b>Fiche de révision (élève)</b> — l'essentiel, les dates
                  clés, les mots à connaître, les erreurs à éviter et un
                  mini-test avec les réponses. Adaptée au niveau de classe.
                </div>
                <div>
                  <b>Fiche de révision DYS</b> — la même fiche, en phrases
                  très courtes, une idée par ligne, mots simples.
                </div>
                <div>
                  <b>Métaphores (résumé imagé)</b> — un court résumé, puis
                  chaque idée clé expliquée par une image de la vie
                  quotidienne, avec sa limite. Adaptée au niveau de classe.
                </div>
                <div>
                  <b>Poème et récitation</b> — le texte ou la notion
                  transformé en poème rimé, facile à apprendre par cœur, suivi
                  de « Ce qu'il faut retenir » et de conseils pour le réciter.
                </div>
                <div>
                  <b>Fiche encyclopédique</b> — tapez le nom d'une personne,
                  d'un lieu, d'une œuvre, d'un concept ou d'un événement, SUX1
                  génère la fiche adaptée (anti-hallucination intégré).
                </div>
                <div>
                  <b>Philo + citations</b> — analyse philosophique avec citations
                  d'auteurs pertinents.
                </div>
                <div>
                  <b>Explication & résolution</b> — pour un exercice ou problème
                  : résolution étape par étape.
                </div>
                <div>
                  <b>Dissertation</b> — dissertation à la française (intro, 3
                  parties, conclusion). Adaptée au niveau de classe.
                </div>
              </div>,
              <div className="space-y-1">
                <div><b>Paragraph</b> — a summary written as one or more flowing paragraphs.</div>
                <div><b>Key points</b> — a bullet list of the essential ideas.</div>
                <div><b>Paragraph + key points</b> — both together, the most complete format.</div>
                <div><b>Teacher</b> — a 5-section teaching sheet (summary, steps, notions, concepts, pitfalls) ready to prepare a lesson.</div>
                <div><b>Revision sheet (pupil)</b> — the essentials, key dates, words to know, mistakes to avoid and a mini-test with answers. Adapted to the class level.</div>
                <div><b>DYS revision sheet</b> — the same sheet in very short sentences, one idea per line, simple words.</div>
                <div><b>Metaphors (illustrated summary)</b> — a short summary, then each key idea explained with an everyday image, and its limit. Adapted to the class level.</div>
                <div><b>Poem and recitation</b> — the text or notion turned into a rhyming poem that is easy to learn by heart, followed by « What to remember » and recitation tips.</div>
                <div><b>Encyclopedic sheet</b> — type the name of a person, place, work, concept or event, SUX1 creates the matching sheet (with built-in anti-hallucination checks).</div>
                <div><b>Philosophy + quotes</b> — a philosophical analysis with quotes from relevant authors.</div>
                <div><b>Explanation & solution</b> — for an exercise or problem: step-by-step solution.</div>
                <div><b>Essay</b> — a French-style essay (introduction, 3 parts, conclusion). Adapted to the class level.</div>
              </div>,
            )}
          />
        </div>
      )}
      {tab !== TAB_BOOK && !formatDisabled && <FormatHelp format={format} />}
      {(
        <SelectField
          label={t("level.label", "Niveau de classe")}
          value={audienceLevel}
          onChange={setAudienceLevel}
          options={[
            { key: "aucun", label: t("level.aucun", "Tous publics (sans niveau)") },
            { key: "primaire", label: t("level.primaire", "Primaire (CP–CM2)") },
            { key: "college", label: t("level.college", "Collège (6ème–3ème)") },
            { key: "lycee", label: t("level.lycee", "Lycée (2nde–Terminale)") },
            { key: "superieur", label: t("level.superieur", "Supérieur (post-bac)") },
          ]}
          testid="dash-audience-level-select"
          tooltip={t("level.tip", "Adapte les mots, les exemples et la longueur au niveau de classe, pour tous les formats. « Tous publics » : résumé standard pour adulte.")}
        />
      )}
      {tab !== TAB_BOOK && format === "biographie" && !formatDisabled && (
        <SelectField
          label={L("Type de fiche", "Sheet type")}
          value={ficheType}
          onChange={setFicheType}
          options={[
            { key: "personne",  label: L("Personne (biographie)", "Person (biography)") },
            { key: "lieu",      label: L("Lieu / monument", "Place / monument") },
            { key: "oeuvre",    label: L("Œuvre (livre, film, tableau…)", "Work (book, film, painting…)") },
            { key: "concept",   label: L("Concept / notion", "Concept / notion") },
            { key: "evenement", label: L("Événement", "Event") },
            { key: "auto",      label: L("Détection automatique", "Automatic detection") },
          ]}
          testid="dash-fiche-type-select"
          note={
            model === "gemini-3-flash" ? (
              <span className="text-[11px] text-[#002FA7]">
                {L(<>Gemini 3 Flash est rapide et économique mais peut inventer des faits sur un sujet peu connu. Pour une fiche vérifiable, préférez <b>Claude Sonnet 4.5</b>.</>,
                   <>Gemini 3 Flash is fast and cheap but may invent facts about a little-known subject. For a verifiable sheet, prefer <b>Claude Sonnet 4.5</b>.</>)}
              </span>
            ) : null
          }
          tooltip={L("Choisissez ce que vous voulez décrire : SUX1 utilise le modèle de fiche adapté (biographie, lieu, analyse d'œuvre, explication de concept, chronologie d'événement). « Détection automatique » laisse l'IA deviner.", "Choose what you want described: SUX1 uses the matching sheet template (biography, place, analysis of a work, concept explanation, event timeline). « Automatic detection » lets the AI guess.")}
        />
      )}
      <SelectField
        label={t("dashboard.output_language")}
        value={language}
        onChange={setLanguage}
        options={L(meta.languages, meta.languages.map((o) => (o.key === "auto" ? { ...o, label: "Automatic detection" } : o)))}
        testid={DASH.languageSelect}
        tooltip={L("Par défaut, le résumé est écrit dans la langue du texte. Choisissez une autre langue pour le traduire en même temps.", "By default, the summary is written in the language of the text. Choose another language to translate it at the same time.")}
      />
      {tab !== TAB_BOOK && (
        <SelectField
          label={t("dashboard.register")}
          value={register}
          onChange={setRegister}
          options={tr(meta.registers)}
          testid="dash-register-select"
          tooltip={L("Ton du résumé : Automatique (suit le texte), Soutenu (écrit officiel, copie), Courant (usage quotidien), Familier (pour parler aux élèves).", "Tone of the summary: Automatic (follows the text), Formal (official writing, exams), Standard (everyday use), Casual (to talk to pupils).")}
        />
      )}
      {tab === TAB_BOOK && (
        <SelectField
          label={t("dashboard.register")}
          value={register}
          onChange={setRegister}
          options={tr(meta.registers)}
          testid="dash-register-select-book"
        />
      )}

      {tab !== TAB_BOOK && (
        <div className="flex flex-col gap-2">
          <label className="sux-marker">{t("dashboard.mode_label")}</label>
          <div className="grid grid-cols-2 gap-0 border border-border">
            <button
              type="button"
              onClick={() => setMode("single")}
              data-testid={DASH.modeSingleBtn}
              data-tip={L("Résumé global : un seul résumé pour l'ensemble du texte.", "Global summary: a single summary for the whole text.")}
              className={`flex items-center justify-center gap-2 py-3 font-mono uppercase tracking-[0.12em] text-[11px] transition-colors border-r border-border ${
                mode === "single"
                  ? "bg-[#002FA7] text-white font-semibold"
                  : "bg-card hover:bg-secondary text-muted-foreground hover:text-foreground"
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              {t("dashboard.mode_single")}
            </button>
            <button
              type="button"
              onClick={() => {
                if (!isPro) {
                  toast.error(t("dashboard.mode_chapters_pro_only"));
                  return;
                }
                setMode("chapters");
              }}
              data-testid={DASH.modeChaptersBtn}
              data-tip={L("Chapitre par chapitre : un résumé séparé pour chaque chapitre, idéal pour un livre (environ 1 crédit pour 3 chapitres).", "Chapter by chapter: a separate summary for each chapter, ideal for a book (about 1 credit per 3 chapters).")}
              className={`flex items-center justify-center gap-2 py-3 font-mono uppercase tracking-[0.12em] text-[11px] transition-colors ${
                mode === "chapters"
                  ? "bg-[#002FA7] text-white font-semibold"
                  : "bg-card hover:bg-secondary text-muted-foreground hover:text-foreground"
              } ${!isPro ? "opacity-60" : ""}`}
            >
              {isPro ? (
                <BookOpen className="w-3.5 h-3.5" />
              ) : (
                <Lock className="w-3.5 h-3.5" />
              )}
              {t("dashboard.mode_chapters")}
            </button>
          </div>
          {mode === "chapters" && isPro && chapterPreview && (
            <div
              data-testid={DASH.chaptersPreview}
              className={`mt-1 border px-3 py-2 font-mono text-[11px] flex items-start gap-2 ${
                chapterPreview.used_fallback
                  ? "border-[#002FA7] text-[#002FA7] bg-[#002FA7]/5"
                  : "border-[#002FA7] text-[#002FA7] bg-[#002FA7]/5"
              }`}
            >
              <BookOpen className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div>
                <div className="font-bold uppercase tracking-[0.15em]">
                  {chapterPreview.used_fallback
                    ? t("dashboard.chapters_fallback", {
                        count: chapterPreview.count,
                      })
                    : t("dashboard.chapters_detected", {
                        count: chapterPreview.count,
                      })}
                </div>
                {chapterPreview.grouped && (
                  <div className="text-[10px] normal-case mt-1" data-testid="chapters-grouped-note">
                    {L(`Gros ouvrage : ${chapterPreview.detected} chapitres regroupés en ${chapterPreview.count} sections (par « Livre » ou « Partie ») pour résumer le livre entier.`,
                       `Large book: ${chapterPreview.detected} chapters grouped into ${chapterPreview.count} sections (by « Book » or « Part ») to summarise the whole book.`)}
                  </div>
                )}
                {chapterPreview.truncated && (
                  <div className="text-[10px] normal-case mt-1 text-[#FF2A00]">
                    {L("Texte au-delà de 4 millions de caractères : seuls les 4 premiers millions seront résumés.", "Text over 4 million characters: only the first 4 million will be summarised.")}
                  </div>
                )}
                {chapterPreview.used_fallback && (
                  <div className="text-[10px] normal-case mt-1 opacity-80">
                    {t("dashboard.chapters_fallback_help")}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      </>)}

      {tab !== TAB_BOOK && (
        <button
          onClick={onSummarize}
          disabled={busy || text.trim().length < 3 || !online}
          data-testid={DASH.summarizeBtn}
          data-tip={L("Résumer : lance le résumé avec les réglages choisis. Le coût en crédits est affiché avant de lancer.", "Summarise: starts the summary with the chosen settings. The credit cost is shown before you start.")}
          className="hidden lg:flex mt-auto bg-[#002FA7] text-white font-mono uppercase tracking-[0.2em] text-sm py-5 hover:bg-[#00227A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed items-center justify-center gap-3 shadow-sm"
          title={!online ? t("offline.button_disabled") : undefined}
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />{" "}
              {t("dashboard.summarizing")}
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" /> {t("dashboard.summarize")}
            </>
          )}
        </button>
      )}
    </aside>
  );
}
